import type { DayObject, ObjectPropertyDefinition } from 'types/graphql'
import { isUUID } from 'validator'

import { hasOptions } from 'src/components/Properties/formattingAndValidation'
import propertyDisplayOverrides from 'src/components/Properties/propertyDisplayOverrides'
import { logger } from 'src/lib/logger'
import { PropertyTypes, type NativeObjectType } from 'src/lib/objects'
import {
  StandardProperties,
  StandardPropertyTablePolicy,
} from 'src/lib/Properties/properties'

export const buildRows = (
  objectType: NativeObjectType,
  objects: DayObject[],
  propertyDefinitions: Record<NativeObjectType, ObjectPropertyDefinition>,
  objectsFilter?: {
    propertyDefinitionId: string
    operator: 'equals' | 'in'
    value: string[] | string
  }
) => {
  if (!objects || !Array.isArray(objects) || !propertyDefinitions) return []
  let rows = objects
    .map((object) => {
      const allProperties = {}

      for (const [propertyId, propertyDefinition] of Object.entries(
        propertyDefinitions
      )) {
        // Formula field support:
        if (propertyDefinition.propertyTypeId === PropertyTypes.Calculated) {
          const standardPropertyDefinition =
            StandardProperties[objectType][propertyId]
          if (standardPropertyDefinition?.formula) {
            const calculatedValue = standardPropertyDefinition.formula(object)
            logger.debug(
              `Calculated value for ${propertyId}: ${calculatedValue}`
            )
            if (!calculatedValue) {
              continue
            }

            allProperties[propertyId] = calculatedValue.value
            object.properties.standard[propertyId] = calculatedValue
          }
        } else {
          const propertyField = isUUID(propertyId) ? 'custom' : 'standard'
          const value = object.properties?.[propertyField]?.[propertyId]?.value

          const isExcluded =
            propertyField === 'standard' &&
            (StandardProperties[objectType]?.[propertyId]?.tablePolicy ===
              StandardPropertyTablePolicy.HIDE ||
              StandardProperties[objectType]?.[propertyId]?.deprecated)

          if (value !== null && value !== undefined && !isExcluded) {
            const override = propertyDisplayOverrides[objectType]?.[propertyId]
            if (typeof override?.tableValueFormatter === 'function') {
              allProperties[propertyId] = override.tableValueFormatter(value)
            } else {
              if (hasOptions(propertyDefinition.propertyTypeId)) {
                if (propertyDefinition) {
                  const options = propertyDefinition.options || []
                  const values =
                    object.properties?.[propertyField]?.[propertyId]?.value
                  if (!values) {
                    allProperties[propertyId] = null
                    continue
                  }
                  const trueOptions = Array.isArray(values)
                    ? values?.filter((value) => value?.value)
                    : []

                  const namesArray =
                    Array.isArray(trueOptions) && trueOptions.length > 0
                      ? trueOptions.map(
                          (value) =>
                            options.find((o) => o.id === value.id)?.name
                        )
                      : []

                  const stringValue = namesArray.join(' ')

                  allProperties[propertyId] = stringValue
                } else {
                  // logger.warn(
                  //   `Property definition not found for property ${propertyId}`
                  // )
                }
              } else {
                allProperties[propertyId] = value
              }
            }
          } else {
            allProperties[propertyId] = null
          }
        }
      }

      const row = {
        ...allProperties,
        id: object.objectId,
        objectType: object.objectType,
        updatedAt: object.updatedAt,
        createdAt: object.createdAt,
        object,
      }

      return row
    })
    .filter((object) => object.id)

  if (objectsFilter && rows.length > 0) {
    rows = rows.filter((row) => {
      const propertyKey = isUUID(objectsFilter.propertyDefinitionId)
        ? 'custom'
        : 'standard'
      const value =
        row.object.properties?.[propertyKey]?.[
          objectsFilter.propertyDefinitionId
        ]?.value

      if (objectsFilter.operator === 'equals') {
        return value === objectsFilter.value
      } else if (
        objectsFilter.operator === 'in' &&
        Array.isArray(objectsFilter.value) &&
        value
      ) {
        const valueInFilter = objectsFilter.value.some((v) => value.includes(v))
        return valueInFilter
      }

      return false
    })
  }

  return rows
}
